/**
 * useWorkflowSourceFormVersion
 *
 * Resolves the form version a workflow run draws its `Values from document`
 * preview and parameter defaults from. Its only consumer is
 * `WorkflowParameterContent`, which is mounted only while the sidebar is open —
 * that is the whole point of resolving here rather than at the call sites.
 *
 * Two sources: the version at `parentPath` when an ArTable supplies one, and
 * otherwise the version `formStore` currently has open. Both leaves are read on
 * every render, including the branch that loses. That is deliberate: a
 * `useSnapshot` whose result goes unread subscribes to the entire proxy, so
 * skipping the read would be broader than making it.
 *
 * The values must come from a snapshot — `mergeDocumentIntoProxy` mutates
 * `fields` in place, so the bare proxy keeps one identity and the sidebar's
 * `resolvedFieldMappingPreview` / `configWarnings` memos, which key on it, would
 * never recompute. They are widened back to their mutable domain types at the
 * return, as the prop boundary used to do; what is handed over is frozen.
 */

import { useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { EntityReferences, Fields } from '#types';
import type { FormVersion } from '#types/System.types';
import { createDocumentStore, formStore } from '@/store';
import { parseFormVersionParentPath } from './formVersionParentPath';

const inactiveFormVersionStore = createDocumentStore<FormVersion>(
  'forms/__inactive__/formVersions/__inactive__',
);

export interface WorkflowSourceFormVersion {
  fields: Fields | undefined;
  entityReferences: EntityReferences | undefined;
  /**
   * True until the backing listener has delivered its first snapshot. Splits the
   * two states `fields === undefined` collapses: still arriving, versus settled
   * on a document that is not there (`persistDocument` writes `data = null` for a
   * missing doc, and `disconnect` restores `loading = true`, so this is true again
   * on every reopen). `FormVersion.fields` is required by the schema, so a settled
   * `undefined` means the source document itself is missing — a real error the
   * consumer must surface rather than wait on forever.
   */
  loading: boolean;
}

export function useWorkflowSourceFormVersion(
  parentPath: string | undefined,
): WorkflowSourceFormVersion {
  const parsed = useMemo(
    () => parentPath ? parseFormVersionParentPath(parentPath) : undefined,
    [parentPath],
  );
  const formId = parsed?.formId;
  const versionId = parsed?.versionId;

  const parentVersionStore = useMemo(
    () => formId && versionId
      ? createDocumentStore<FormVersion>(`forms/${formId}/formVersions/${versionId}`)
      : inactiveFormVersionStore,
    [formId, versionId],
  );

  useEffect(() => {
    if (!formId || !versionId) return;
    parentVersionStore.connect();
    return () => parentVersionStore.disconnect();
  }, [formId, versionId, parentVersionStore]);

  const parentSnap = useSnapshot(parentVersionStore.state);
  const openSnap = useSnapshot(formStore.state);

  const parentFields = parentSnap.data?.fields as Fields | undefined;
  const parentEntityReferences = parentSnap.data?.entityReferences as EntityReferences | undefined;
  const parentLoading = parentSnap.loading;
  const openFields = openSnap.formVersion.data?.fields as Fields | undefined;
  const openEntityReferences = openSnap.formVersion.data?.entityReferences as EntityReferences | undefined;
  const openLoading = openSnap.formVersion.loading;

  return parsed
    ? { fields: parentFields, entityReferences: parentEntityReferences, loading: parentLoading }
    : { fields: openFields, entityReferences: openEntityReferences, loading: openLoading };
}
